<template>
  <modal :show="show" @update:show="handleModalClose" class="dark-modal">
    <div class="modal-box">
      <div class="modal-box-header">
        <h2 class="modal-title">{{ modalTitle }}</h2>
      </div>

      <form @submit.prevent="submit">
        <div class="field">
          <label class="label">
            {{ $t('indexes.indexName') }}<span class="required-asterisk">*</span>
            <popover placement="top" width="300px">
              <span slot="trigger" class="icon is-small has-text-info" style="cursor: help;">
                <octicon :icon="info" />
              </span>
              <div class="has-text-left is-size-7">
                {{ $t('indexes.tooltips.name') }}
              </div>
            </popover>
          </label>
          <div class="control">
            <input
              v-model="formData.name"
              class="input"
              type="text"
              :class="{ 'is-danger': validationErrors.name }"
              :placeholder="$t('indexes.placeholders.name')"
              :disabled="isEditMode"
              maxlength="30"
              @input="handleNameInput"
              @blur="handleNameBlur"
            >
          </div>
          <p v-if="validationErrors.name" class="help is-danger">
            {{ validationErrors.name }}
          </p>
        </div>

        <div class="field">
          <label class="label">
            {{ $t('indexes.indexDescription') }}
            <popover placement="top" width="300px">
              <span slot="trigger" class="icon is-small has-text-info" style="cursor: help;">
                <octicon :icon="info" />
              </span>
              <div class="has-text-left is-size-7">
                {{ $t('indexes.tooltips.description') }}
              </div>
            </popover>
          </label>
          <div class="control">
            <textarea
              v-model="formData.description"
              class="textarea"
              rows="3"
              :class="{ 'is-danger': validationErrors.description }"
              :placeholder="$t('indexes.placeholders.description')"
              maxlength="4096"
              @input="handleDescriptionInput"
              @blur="handleDescriptionBlur"
            ></textarea>
          </div>
          <p v-if="validationErrors.description" class="help is-danger">
            {{ validationErrors.description }}
          </p>
          <p class="help" :class="{ 'has-text-warning': descriptionCharacterCount > maxDescriptionLength * 0.9, 'has-text-danger': descriptionCharacterCount > maxDescriptionLength }">
            {{ descriptionCharacterCount }} / {{ maxDescriptionLength }}
          </p>
        </div>

        <template>
          <div style="width: 50%; float: left; padding-right: 0.5rem;">
            <label class="label">
              {{ $t('indexes.maxSize') }}<span class="required-asterisk">*</span>
              <popover placement="top" width="300px">
                <span slot="trigger" class="icon is-small has-text-info" style="cursor: help;">
                  <octicon :icon="info" />
                </span>
                <div class="has-text-left is-size-7">
                  {{ $t('indexes.tooltips.maxSize') }}
                </div>
              </popover>
            </label>
            <div class="field has-addons">
              <p class="control">
                <input
                  v-model="formData.max_size"
                  class="input"
                  type="text"
                  :class="{ 'is-danger': validationErrors.max_size }"
                  :placeholder="$t('indexes.placeholders.maxSize')"
                  @input="handleMaxSizeInput"
                  @keypress="handleNumericKeypress"
                  @paste="handleMaxSizePaste"
                  @blur="handleMaxSizeBlur"
                >
              </p>
              <p class="control">
                <span class="select">
                  <select v-model="formData.unit">
                    <option disabled value="">{{ $t('unit') }}</option>
                    <option v-for="unit in units" :key="unit" :value="unit">{{ unit.toUpperCase() }}</option>
                  </select>
                </span>
              </p>
            </div>
            <p v-if="validationErrors.max_size" class="help is-danger">
              {{ validationErrors.max_size }}
            </p>
          </div>

          <div style="width: 50%; float: right; padding-left: 0.5rem;">
            <label class="label">
              {{ $t('indexes.maxStorageTime') }}<span class="required-asterisk">*</span>
              <popover placement="top" width="300px">
                <span slot="trigger" class="icon is-small has-text-info" style="cursor: help;">
                  <octicon :icon="info" />
                </span>
                <div class="has-text-left is-size-7">
                  {{ $t('indexes.tooltips.rotateTime') }}
                </div>
              </popover>
            </label>
            <div class="field has-addons">
              <p class="control">
                <input
                  v-model="formData.rotate_time"
                  class="input"
                  type="text"
                  :class="{ 'is-danger': validationErrors.rotate_time }"
                  :placeholder="$t('indexes.placeholders.rotateTime')"
                  @input="handleRotateTimeInput"
                  @keypress="handleNumericKeypress"
                  @paste="handleRotateTimePaste"
                  @blur="handleRotateTimeBlur"
                >
              </p>
              <p class="control">
                <span class="select">
                  <select disabled>
                    <option selected="true">{{ $t('indexes.unitDay') }}</option>
                  </select>
                </span>
              </p>
            </div>
            <p v-if="validationErrors.rotate_time" class="help is-danger">
              {{ validationErrors.rotate_time }}
            </p>
          </div>
        </template>

        <div class="modal-footer">
          <button type="button" class="button cancel-button" @click.prevent="handleCancel" :disabled="isLoading">
            {{ $t('indexes.cancelButton') }}
          </button>
          <button type="submit" class="button is-primary" :class="{'is-loading': isLoading}" :disabled="isLoading">
            {{ $t('indexes.confirmButton') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Cancel Confirmation Modal -->
    <modal :show.sync="showCancelConfirm" class="cancel-confirm-modal">
      <div class="confirm-box">
        <div class="confirm-header">
          <h3>{{ $t('indexes.cancelTitle') }}</h3>
        </div>
        <div class="confirm-body">
          <p>{{ $t('indexes.cancelMessage') }}</p>
        </div>
        <div class="confirm-footer">
          <button
            type="button"
            class="button cancel-button"
            @click="showCancelConfirm = false"
          >
            {{ $t('cancel') }}
          </button>
          <button
            type="button"
            class="button is-primary"
            @click="confirmCancel"
          >
            {{ $t('confirm') }}
          </button>
        </div>
      </div>
    </modal>
  </modal>
</template>

<script>
import { Modal, Popover } from '@cyradar/ui'
import Octicon, { info } from 'octicons-vue'
import { validateIndexName, validateIndexDescription, validateMaxSize, validateRotateTime, extractNumericValue } from '@/utils/indexValidation'

const units = ['mb', 'gb', 'tb']

export default {
  name: 'IndexFormModal',
  components: { Modal, Popover, Octicon },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    isEditMode: {
      type: Boolean,
      default: false
    },
    editItem: {
      type: Object,
      default: null
    },
    existingIndexes: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      formData: {
        name: '',
        description: '',
        max_size: 10,
        rotate_time: 90,
        unit: 'gb',
      },
      validationErrors: {
        name: '',
        description: '',
        max_size: '',
        rotate_time: ''
      },
      descriptionCharacterCount: 0,
      maxDescriptionLength: 4096,
      isLoading: false,
      showCancelConfirm: false
    }
  },
  computed: {
    info() {
      return info
    },
    units() {
      return units
    },
    modalTitle() {
      if (this.isEditMode) {
        return this.$t('indexes.editIndexTitle', { indexName: this.formData.name || '' })
      }
      return this.$t('indexes.addIndexTitle')
    }
  },
  watch: {
    show(val) {
      if (val) {
        if (this.isEditMode && this.editItem) {
          this.loadEditData()
        } else {
          this.resetForm()
        }
      }
    }
  },
  methods: {
    /**
     * Validates index name and updates validation state
     */
    async validateName() {
      const validation = validateIndexName(this.formData.name, (key, params) => this.$t(key, params), this.existingIndexes, this.isEditMode)
      this.validationErrors.name = validation.error
      return validation.isValid
    },

    /**
     * Validates description and updates character count
     */
    validateDescription() {
      const validation = validateIndexDescription(this.formData.description, (key, params) => this.$t(key, params))
      this.validationErrors.description = validation.error
      this.descriptionCharacterCount = validation.characterCount
      this.maxDescriptionLength = validation.maxLength
      return validation.isValid
    },

    /**
     * Validates max size and applies auto-correction
     */
    validateMaxSizeField() {
      const validation = validateMaxSize(this.formData.max_size, this.formData.unit, (key, params) => this.$t(key, params))
      this.validationErrors.max_size = validation.error

      if (validation.wasAutoCorrected) {
        this.formData.max_size = validation.correctedValue
      }

      return validation.isValid
    },

    /**
     * Validates rotate time and applies auto-correction
     */
    validateRotateTimeField() {
      const validation = validateRotateTime(this.formData.rotate_time, (key, params) => this.$t(key, params))
      this.validationErrors.rotate_time = validation.error

      if (validation.wasAutoCorrected) {
        this.formData.rotate_time = validation.correctedValue
      }

      return validation.isValid
    },

    /**
     * Validates all form fields
     */
    async validateForm() {
      const nameValid = await this.validateName()
      const descriptionValid = this.validateDescription()
      const maxSizeValid = this.validateMaxSizeField()
      const rotateTimeValid = this.validateRotateTimeField()

      return nameValid && descriptionValid && maxSizeValid && rotateTimeValid
    },

    /**
     * Clears validation error for a specific field
     */
    clearFieldError(field) {
      this.validationErrors[field] = ''
    },

    /**
     * Handles name input events
     */
    handleNameInput() {
      this.clearFieldError('name')
      // Limit to 30 characters
      if (this.formData.name && this.formData.name.length > 30) {
        this.formData.name = this.formData.name.substring(0, 30)
      }
    },

    /**
     * Handles name blur events
     */
    async handleNameBlur() {
      await this.validateName()
    },

    /**
     * Handles description input events
     */
    handleDescriptionInput() {
      this.clearFieldError('description')
      // Limit to 4096 characters
      if (this.formData.description && this.formData.description.length > 4096) {
        this.formData.description = this.formData.description.substring(0, 4096)
      }
      this.validateDescription()
    },

    /**
     * Handles description blur events
     */
    handleDescriptionBlur() {
      this.validateDescription()
    },

    /**
     * Handles numeric keypress events to prevent non-numeric input
     */
    handleNumericKeypress(event) {
      const charCode = event.which ? event.which : event.keyCode
      // Allow only numbers (0-9) and decimal point (.)
      if ((charCode < 48 || charCode > 57) && charCode !== 46) {
        event.preventDefault()
      }
      // Prevent multiple decimal points
      if (charCode === 46 && this.formData.max_size && this.formData.max_size.toString().includes('.')) {
        event.preventDefault()
      }
    },

    /**
     * Handles max size input events
     */
    handleMaxSizeInput() {
      this.clearFieldError('max_size')
      // Extract numeric value and update field
      const numericValue = extractNumericValue(this.formData.max_size)
      if (numericValue !== null) {
        this.formData.max_size = numericValue
      }
    },

    /**
     * Handles max size paste events
     */
    handleMaxSizePaste(event) {
      event.preventDefault()
      const pastedText = (event.clipboardData || window.clipboardData).getData('text')
      const numericValue = extractNumericValue(pastedText)
      if (numericValue !== null) {
        this.formData.max_size = numericValue
        this.clearFieldError('max_size')
      }
    },

    /**
     * Handles max size blur events
     */
    handleMaxSizeBlur() {
      // Set default value if empty
      if (!this.formData.max_size || this.formData.max_size === '') {
        this.formData.max_size = 10
      }
      this.validateMaxSizeField()
    },

    /**
     * Handles rotate time input events
     */
    handleRotateTimeInput() {
      this.clearFieldError('rotate_time')
      // Extract numeric value and update field
      const numericValue = extractNumericValue(this.formData.rotate_time)
      if (numericValue !== null) {
        this.formData.rotate_time = numericValue
      }
    },

    /**
     * Handles rotate time paste events
     */
    handleRotateTimePaste(event) {
      event.preventDefault()
      const pastedText = (event.clipboardData || window.clipboardData).getData('text')
      const numericValue = extractNumericValue(pastedText)
      if (numericValue !== null) {
        this.formData.rotate_time = numericValue
        this.clearFieldError('rotate_time')
      }
    },

    /**
     * Handles rotate time blur events
     */
    handleRotateTimeBlur() {
      this.validateRotateTimeField()
    },

    handleModalClose(value) {
      // Prevent modal from closing on outside click or ESC
      // Only allow closing through cancel button inside the form
      // Do nothing - modal will only close through closeModal()
    },

    handleCancel() {
      // Close modal directly without confirmation
      this.closeModal()
    },

    confirmCancel() {
      // User confirmed cancellation
      this.showCancelConfirm = false
      this.closeModal()
    },

    closeModal() {
      this.$emit('update:show', false)
      // Clear validation errors when closing modal
      this.validationErrors = {
        name: '',
        description: '',
        max_size: '',
        rotate_time: ''
      }
      this.descriptionCharacterCount = 0
    },

    async submit() {
      // Validate form before submission
      const isValid = await this.validateForm()

      if (!isValid) {
        // Focus on first field with error
        this.$nextTick(() => {
          const firstErrorField = ['name', 'description', 'max_size', 'rotate_time'].find(field => this.validationErrors[field])
          if (firstErrorField) {
            const input = this.$el.querySelector(`[name="${firstErrorField}"], input[v-model*="${firstErrorField}"]`)
            if (input) {
              input.focus()
            }
          }
        })

        return
      }

      this.isLoading = true

      // Convert values to proper types
      const payload = {
        ...this.formData,
        max_size: parseFloat(this.formData.max_size),
        rotate_time: parseFloat(this.formData.rotate_time)
      }

      try {
        const apiMethod = this.isEditMode ? 'patch' : 'post'
        const apiUrl = this.isEditMode ? `/api/v1/indexes/${this.formData.id}` : '/api/v1/indexes'
        const response = await this.$http[apiMethod](apiUrl, payload)

        if (response && response.data) {
          const successMessage = this.isEditMode
            ? this.$t('indexes.updateSuccess')
            : this.$t('indexes.createSuccess')

          this.$store.dispatch('NOTIFY', {
            type: 'success',
            text: successMessage
          })

          this.$emit('success')
          this.closeModal()
        }
      } catch (error) {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          error
        })
      } finally {
        this.isLoading = false
      }
    },

    resetForm() {
      this.formData = {
        name: '',
        description: '',
        max_size: 10,
        rotate_time: 90,
        unit: 'gb',
      }

      // Clear validation errors
      this.validationErrors = {
        name: '',
        description: '',
        max_size: '',
        rotate_time: ''
      }
      this.descriptionCharacterCount = 0
    },

    loadEditData() {
      if (!this.editItem) return

      // Deep clone the edit item
      this.formData = JSON.parse(JSON.stringify(this.editItem))

      // Clear validation errors
      this.validationErrors = {
        name: '',
        description: '',
        max_size: '',
        rotate_time: ''
      }

      // Initialize character count for description
      this.descriptionCharacterCount = this.formData.description ? this.formData.description.length : 0
    },

  }
}
</script>

<style lang="scss">
@import '@/assets/styles/dark-modal.scss';

// Additional custom styles specific to Index Form Modal
.dark-modal {
  .status-field {
    clear: both;
    padding-top: 1rem;

    .status-label {
      font-weight: 600;
      margin-bottom: 0.75rem;
    }

    .status-toggle {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    .status-text {
      color: $modal-text;
      font-weight: 500;
    }
  }

  .switch {
    & + label {
      color: $modal-text;
    }
  }

  .modal-footer {
    clear: both;
    margin-top: 1.5rem;
  }

  .help.has-text-warning {
    color: #ffdd57;
  }
}

// Cancel Confirmation Modal Styles
.cancel-confirm-modal {
  .confirm-box {
    background-color: $modal-bg;
    border-radius: 8px;
    padding: 2rem;
    max-width: 500px;

    .confirm-header {
      margin-bottom: 1.5rem;

      h3 {
        color: $modal-text;
        font-size: 1.25rem;
        font-weight: 600;
        margin: 0;
      }
    }

    .confirm-body {
      margin-bottom: 2rem;

      p {
        color: $modal-text-secondary;
        font-size: 1rem;
        line-height: 1.5;
        margin: 0;
      }
    }

    .confirm-footer {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;

      .button {
        @include dark-modal-cancel-button;

        &.is-primary {
          @include dark-modal-primary-button;
        }
      }
    }
  }
}

// Toggle switch styles
.rotate-toggle-field {
  .toggle-container {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .toggle-label {
    color: var(--modal-text, #e0e0e0);
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .toggle-switch {
    width: 48px;
    height: 24px;
    background-color: var(--toggle-bg-inactive, #4a4a4a);
    border-radius: 12px;
    cursor: pointer;
    transition: background-color 0.3s ease;
    position: relative;

    &.is-active {
      background-color: var(--toggle-bg-active, #3273dc);
    }

    .toggle-slider {
      width: 20px;
      height: 20px;
      background-color: white;
      border-radius: 50%;
      position: absolute;
      top: 2px;
      left: 2px;
      transition: transform 0.3s ease;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    &.is-active .toggle-slider {
      transform: translateX(24px);
    }
  }
}
</style>
